<template>
  <div
    @mouseenter="$emit('hover', true)"
    @mouseleave="$emit('hover', false)"
    class="fancy-chooser"
    :class="{ '--is-selected' : isSelected, '--has-glowing-animation': hasGlowingAnimation, '--is-disabled' : isDisabled }"
  >
    <div
      @click="isDisabled ? false : $emit('click')"
      class="fancy-chooser__content"
    >
      <div
        v-if="$slots.icon && !$slots.pic"
        class="fancy-chooser__icon"
      >
        <slot name="icon"></slot>
      </div>

      <div
        v-if="$slots.pic && !$slots.icon"
        class="fancy-chooser__pic"
      >
        <slot name="pic"></slot>
      </div>

      <div class="fancy-chooser__info">
        <b class="fancy-chooser__title"><slot name="title"></slot></b>
        <br class="u-dp-none">
        <span
          v-if="$slots.description !== undefined"
          class="fancy-chooser__description"
        >
          <slot name="description"></slot>
        </span>
      </div>
    </div>

    <div
      v-if="help && help.url"
      class="fancy-chooser__footer"
    >
      <span
        @click="$root.$emit('composerModal:open', 'ModalExternalContent', { url: help.url })"
        class="fancy-chooser__help"
      >
        <i class="icon icon-question-circle"></i> {{ help.text ? help.text : 'Details anzeigen' }}
      </span>
    </div>

    <i class="icon icon-check-circle fancy-chooser__check"></i>

    <Badge
      :color="'yellow'"
      v-if="isDisabled && minQuantity"
    >
      ab {{ minQuantity }} Präsenten
    </Badge>
    <Badge
      :color="'yellow'"
      v-if="isDisabled && maxQuantity"
    >
      bis {{ maxQuantity }} Präsenten
    </Badge>
  </div>
</template>

<script>
import Badge from '@/components/Composer/Badge';

export default {
  name: 'FancyChooser',
  components: {
    Badge
  },
  props: {
    isSelected: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    minQuantity: {
      type: Number,
      default: 0
    },
    maxQuantity: {
      type: Number,
      default: 0
    },
    // Define whether the chooser throws a single-time glow animation to draw attention to the user
    hasGlowingAnimation: {
      type: Boolean,
      default: false
    },
    help: {
      type: Object
    }
  },
  data () {
    return {
    };
  }
};
</script>

<style lang="scss" scoped>
/**
 * Fancy Chooser
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Fancy chooser that acts like a "radio button" but for more presentable and beauty options
 * @since 2021-08-19
**/

$component: 'fancy-chooser';

.#{$component} {
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid $sky;
  border-radius: $border-radius-lg;
  background-color: $white;
  cursor: pointer;
  transition: box-shadow 0.1s ease-in-out;

  &:not(.--is-selected) {
    @include hover () {
      box-shadow: 0px 0px 10px rgba($primary, 0.3);

      .#{$component}__check {
        display: block;

        &:before {
          opacity: 0.5;
        }
      }
    }
  }

  &.--is-selected {
    border: 2px solid $primary;
    box-shadow: 0px 0px 10px rgba($primary, 0.3);

    .#{$component} {
      &__content {
        padding: 9px;
      }
      &__footer {
        padding-left: 9px;
      }

      &__icon {
        color: $primary;

        svg {
          color: $primary;
        }

        path {
          stroke: $primary;
        }
      }

      &__check {
        display: block;
      }
    }
  }

  @keyframes glowing {
    0% {
      box-shadow: 0px 0px 0 rgba($primary, 0.3);
    }

    10% {
      box-shadow: 0px 0px 20px rgba($primary, 0.3);
    }

    30% {
      box-shadow: 0px 0px 0 rgba($primary, 0.3);
    }

    40% {
      box-shadow: 0px 0px 0 rgba($primary, 0.3);
    }

    50% {
      box-shadow: 0px 0px 0 rgba($primary, 0.3);
    }

    60% {
      box-shadow: 0px 0px 0 rgba($primary, 0.3);
    }

    70% {
      box-shadow: 0px 0px 0 rgba($primary, 0.3);
    }

    80% {
      box-shadow: 0px 0px 0 rgba($primary, 0.3);
    }

    100% {
      box-shadow: 0px 0px 0 rgba($primary, 0.3);
    }
  }

  &.--has-glowing-animation {
    animation: glowing 3s infinite;
    animation-delay: 0.6s;

    @include hover () {
      animation: none;
    }
  }

  &.--is-disabled {
    cursor: default;

    .#{$component} {
      &__icon,
      &__info {
        opacity: 0.6;
      }

      &__content {
        pointer-events: none;
      }

      &__help {
        cursor: pointer;
      }
    }

    @include hover () {
      box-shadow: none;

      .#{$component}__check {
        display: none;
      }
    }
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    padding: 10px;
  }

  &__icon {
    @include rfs(2.6rem, flex-basis);
    @include margin-right(map-get($spacers, 1) * 2.6);

    svg {
      width: 100%;
      height: auto;
      aspect-ratio: 1 / 1;
      color: $ink-lighter;

      path {
        stroke: $ink-lighter;
      }
    }
  }

  &__pic {
    @include rfs(4.2rem, flex-basis);
    @include margin-right(map-get($spacers, 1) * 2.6);
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    word-break: break-word;
  }

  &__title {
    color: $ink-light;
    font-weight: $font-weight-normal;
    line-height: $line-height-base-sm;
  }

  &__description {
    @include margin-top(map-get($spacers, 1));
    color: $ink-lighter;
    @include font-size($font-size-sm);
    line-height: $line-height-base-sm;

    > span {
      color: $ink-lightest;
    }

    small {
      @include font-size($font-size-xs);
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    @include padding(map-get($spacers, 1) map-get($spacers, 2) map-get($spacers, 1) 10px);
    border-top: 1px solid $gray-ghost-fixed;
    border-bottom-left-radius: $border-radius-lg;
    border-bottom-right-radius: $border-radius-lg;
    background-color: $sky-lighter;
    color: $ink-lightest;
    @include font-size($font-size-sm);
  }

  &__help {
    display: flex;
    align-items: center;

    .icon {
      @include margin-right(map-get($spacers, 1));
      @include font-size($font-size-base * 1.2);
    }

    @include hover () {
      color: $ink-lighter;
    }

    &:active {
      color: $ink-light;
    }
  }

  &__check {
    display: none;
    position: absolute;
    width: $font-size-base * 1.6;
    height: $font-size-base * 1.6;
    @include rfs(- $font-size-base * 0.7, right);
    top: 10px;
    border: 2px solid $white;
    border-radius: 50%;
    background-color: $white;
    font-size: $font-size-base * 1.4;
    color: $primary;
  }

  ::v-deep .badge {
    position: absolute;
    top: -0.5rem;
    right: 1rem;
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-md')) {
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-sm')) {
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-xs')) {
  }
}
</style>
